import React, { useState, useEffect } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useTheme } from 'components/core/theme-provider';

/**
 * ThemeColorControl Component
 * 
 * A comprehensive UI component for customizing the primary color theme in Light Mode only.
 * Provides color picker, preset colors, and real-time preview of theme changes.
 * 
 * Features:
 * - Color picker input for custom primary color selection
 * - Preset color options for quick selection
 * - Real-time theme preview
 * - localStorage persistence for custom colors
 * - Light mode only customization (dark mode colors remain unchanged)
 * - HSL color format conversion utilities
 * - Reset to default functionality
 * 
 * @returns {JSX.Element} Theme color control interface
 */


// Default primary color (current theme color)
const DEFAULT_PRIMARY_COLOR = '#AC9477'; // HSL(33, 24%, 57%) converted to hex

// Preset color options
const PRESET_COLORS = [
    { name: 'Default Brown', hex: '#AC9477' },
    { name: 'Ocean Blue', hex: '#0EA5E9' },
    { name: 'Emerald Green', hex: '#10B981' },
    { name: 'Purple', hex: '#8B5CF6' },
    { name: 'Rose', hex: '#F43F5E' },
    { name: 'Orange', hex: '#F97316' },
    { name: 'Teal', hex: '#14B8A6' },
    { name: 'Indigo', hex: '#6366F1' },
];

export function ThemeColorControl() {
    const { theme, primaryColor, setPrimaryColor } = useTheme();
    const [selectedColor, setSelectedColor] = useState<string>(primaryColor);
    const [isCustomColor, setIsCustomColor] = useState(false);

    // Sync with theme provider's primary color
    useEffect(() => {
        setSelectedColor(primaryColor);
        setIsCustomColor(primaryColor !== DEFAULT_PRIMARY_COLOR);
    }, [primaryColor]);


    // Handle color selection from presets
    const handlePresetColorSelect = (colorHex: string) => {
        setSelectedColor(colorHex);
        setIsCustomColor(colorHex !== DEFAULT_PRIMARY_COLOR);
        setPrimaryColor(colorHex);
    };

    // Handle custom color input change
    const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const colorHex = event.target.value;
        setSelectedColor(colorHex);
        setIsCustomColor(true);
        setPrimaryColor(colorHex);
    };

    // Reset to default color
    const handleReset = () => {
        setSelectedColor(DEFAULT_PRIMARY_COLOR);
        setIsCustomColor(false);
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    };

    return (
        <div className="bg-background p-6 rounded-lg border border-border shadow-sm">
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                        Primary Color Theme
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Customize the primary color theme. This affects both light and dark modes with appropriate shades.
                    </p>
                    {theme === 'dark' && (
                        <p className="text-sm text-success mt-2 bg-success-background px-3 py-2 rounded-md">
                            ✨ You&apos;re in Dark Mode. Color changes will affect both light and dark mode appropriately.
                        </p>
                    )}
                </div>

                {/* Color Picker */}
                <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                        Custom Color
                    </label>
                    <div className="flex items-center gap-3">
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={handleCustomColorChange}
                            className="w-12 h-12 rounded-md border border-border cursor-pointer bg-transparent"
                            title="Select custom primary color"
                        />
                        <Input
                            type="text"
                            value={selectedColor}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^#[0-9A-F]{6}$/i.test(value)) {
                                    handleCustomColorChange(e);
                                }
                            }}
                            placeholder="#AC9477"
                            className="font-mono text-sm max-w-32"
                        />
                    </div>
                </div>

                {/* Preset Colors */}
                <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                        Preset Colors
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                        {PRESET_COLORS.map((preset) => (
                            <button
                                key={preset.hex}
                                onClick={() => handlePresetColorSelect(preset.hex)}
                                className={`relative group p-3 rounded-lg border-2 transition-all hover:scale-105 ${selectedColor === preset.hex
                                    ? 'border-primary shadow-md'
                                    : 'border-border hover:border-muted-foreground'
                                    }`}
                                title={preset.name}
                            >
                                <div
                                    className="w-full h-8 rounded-md mb-2"
                                    style={{ backgroundColor: preset.hex }}
                                />
                                <span className="text-xs text-muted-foreground group-hover:text-foreground">
                                    {preset.name}
                                </span>
                                {selectedColor === preset.hex && (
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                        <svg
                                            className="w-2.5 h-2.5 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preview Section */}
                <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                        Preview
                    </label>
                    <div className="p-4 bg-muted rounded-lg space-y-3">
                        <div className="flex gap-2">
                            <Button size="sm">Primary Button</Button>
                            <Button variant="outline" size="sm">Outline Button</Button>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            Selected color: <span className="font-mono text-primary">{selectedColor}</span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-3 border-t border-border">
                    <Button
                        onClick={handleReset}
                        variant="outline"
                        disabled={!isCustomColor}
                        className="flex-1"
                    >
                        Reset to Default
                    </Button>
                </div>
            </div>
        </div>
    );
}
